import { z } from 'zod';
import type { CsvApplicantRow, CsvContract } from './types';

const EMPTY_LABELS = new Set(['n/a', '<none specified>']);

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => v || undefined);

const label = (max: number) => text(max).transform((v) => (v && !EMPTY_LABELS.has(v.toLowerCase()) ? v : undefined));

const veracrossId = text(64).transform((v) => (v && !/^0+$/.test(v) ? v : undefined));

function toIsoDate(v: string): string | undefined {
  const us = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  const iso = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const parts = us ? [us[3]!.padStart(4, '20'), us[1], us[2]] : iso?.slice(1);
  if (!parts) return undefined;
  const [y, m, d] = parts.map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d ? date.toISOString().slice(0, 10) : undefined;
}

const calendarDate = text(32).transform((v, ctx) => {
  if (!v) return undefined;
  const iso = toIsoDate(v);
  if (!iso) {
    ctx.addIssue({ code: 'custom', message: `Unrecognised date "${v}" (expected MM/DD/YY)` });
    return z.NEVER;
  }
  return iso;
});

const RawRowV2 = z.object({
  personId: z.string().trim().min(1, 'Person ID is required').max(64),
  person: text(250),
  firstName: text(120),
  lastName: text(120),
  applicationId: veracrossId,
  householdId: veracrossId,
  schoolYear: text(32),
  gradeApplying: text(32),
  admissionDecision: label(120),
  coreValue1: text(25_000),
  coreValue2: text(25_000),
  middleSchoolCoreValue1: text(25_000),
  middleSchoolCoreValue2: text(25_000),
  upperSchoolCoreValue1: text(25_000),
  upperSchoolCoreValue2: text(25_000),
  campusApplying: label(120),
  admissionSource: label(120),
  appliedPreviously: text(120),
  enrollmentDecision: label(120),
  enrollmentDecisionDate: calendarDate,
  applicationDate: calendarDate,
  inquiryDate: calendarDate,
});
type RawField = keyof z.input<typeof RawRowV2>;

export function splitPersonName(person: string | undefined): { firstName?: string; lastName?: string } {
  const comma = person?.indexOf(',') ?? -1;
  if (!person || comma < 0) return {};
  const lastName = person.slice(0, comma).trim();
  const firstName = person.slice(comma + 1).trim();
  return lastName && firstName ? { firstName, lastName } : {};
}

const CORE_VALUE_FIELDS = [
  ['Core Value 1', 'coreValue1'],
  ['Core Value 2', 'coreValue2'],
  ['Middle School Student Core Value 1', 'middleSchoolCoreValue1'],
  ['Middle School Student Core Value 2', 'middleSchoolCoreValue2'],
  ['Upper School Student Core Value 1', 'upperSchoolCoreValue1'],
  ['Upper School Student Core Value 2', 'upperSchoolCoreValue2'],
] as const;

export const CsvApplicantRowV2: z.ZodType<CsvApplicantRow> = RawRowV2.transform((r) => ({
  veracrossPersonId: r.personId,
  veracrossApplicationId: r.applicationId,
  veracrossHouseholdId: r.householdId,
  ...(r.firstName && r.lastName ? { firstName: r.firstName, lastName: r.lastName } : splitPersonName(r.person)),
  gradeApplying: r.gradeApplying,
  schoolYear: r.schoolYear,
  admissionStatus: r.admissionDecision,
  campusApplying: r.campusApplying,
  admissionSource: r.admissionSource,
  appliedPreviously: r.appliedPreviously,
  enrollmentDecision: r.enrollmentDecision,
  enrollmentDecisionDate: r.enrollmentDecisionDate,
  applicationDate: r.applicationDate,
  inquiryDate: r.inquiryDate,
  narrative:
    CORE_VALUE_FIELDS.filter(([, f]) => r[f])
      .map(([heading, f]) => `${heading}:\n${r[f]}`)
      .join('\n\n') || undefined,
}));

export const CSV_CONTRACT_V2: CsvContract<CsvApplicantRow, RawField> = {
  version: 'v2-veracross-query',
  columns: [
    { header: 'CANDIDATE: Person ID', field: 'personId', isId: true, aliases: ['Person ID'] },
    { header: 'Person', field: 'person', optional: true },
    { header: 'CANDIDATE: First Name', field: 'firstName', optional: true, aliases: ['First Name'] },
    { header: 'CANDIDATE: Last Name', field: 'lastName', optional: true, aliases: ['Last Name'] },
    { header: 'APPLICATION: Application ID', field: 'applicationId', optional: true, aliases: ['Application ID'] },
    {
      header: 'Household ID',
      field: 'householdId',
      optional: true,
      aliases: ['HOUSEHOLD: Household ID', 'CANDIDATE: Household ID'],
    },
    { header: 'Year Applying For', field: 'schoolYear', optional: true },
    { header: 'Grade Applying For', field: 'gradeApplying', optional: true },
    { header: 'Admission Decision', field: 'admissionDecision', optional: true },
    { header: 'Core Value 1', field: 'coreValue1', aliases: ['Application Core Values Question 1'] },
    { header: 'Core Value 2', field: 'coreValue2', aliases: ['Application Core Values Question 2'] },
    { header: 'Middle School Student Questionnaire Core Values Question 1', field: 'middleSchoolCoreValue1', optional: true },
    { header: 'Middle School Student Questionnaire Core Values Question 2', field: 'middleSchoolCoreValue2', optional: true },
    { header: 'Upper School Student Questionnaire Core Values Question 1', field: 'upperSchoolCoreValue1', optional: true },
    { header: 'Upper School Student Questionnaire Core Values Question 2', field: 'upperSchoolCoreValue2', optional: true },
    { header: 'Campus Applying For', field: 'campusApplying', optional: true },
    { header: 'Admission Source', field: 'admissionSource', optional: true },
    { header: 'Applied Previously', field: 'appliedPreviously', optional: true },
    { header: 'Enrollment Decision', field: 'enrollmentDecision', optional: true },
    { header: 'Enrollment Decision Date', field: 'enrollmentDecisionDate', optional: true },
    { header: 'Application Date', field: 'applicationDate', optional: true },
    { header: 'Inquiry Date', field: 'inquiryDate', optional: true },
  ],
  ignoredHeaders: [
    'Person Admission ID',
    'Admissions Candidate Profile',
    'Roles',
    'Resident Status Applying For',
    'Application',
    'Admission Decision Date',
  ],
  rowSchema: CsvApplicantRowV2,
};
